// packages/web/src/lib/submissions.ts — B20a: the small shared vocabulary
// the submissions screens need and AuditionSpread/RoleSurface's own AA/LINK
// (lib/auditionSpread.ts) do not carry, because that file's own header
// scopes it to the four Project › Role › Audition screens. `aa_submission`
// is a fifth, later type (F1) and gets its own tiny file rather than being
// folded into a constant object whose own guard (packLinkKeys.test.ts)
// checks it against those four screens' history, not this one's.
//
// The type key and relationship keys below are copied verbatim from
// packages/verticals/actor-assist/src/recordTypes.ts's own §20 declaration
// (mirrored the same way actorSubmissions.ts's own header states its
// copies exist) — never re-typed by a second guess.
import type { LinkRow, RecordRow, SchemaLifecycle } from './api';
import { stepsFor, type Step } from './auditionSpread';

export const SUBMISSION_TYPE = 'aa_submission';

export const SUBMISSION_LINK = {
  /** aa_submission → aa_subject (LOOKUP — prefixed, deliberately: keeps
   * aa_audition.subject the one bare subject hop in the pack). */
  SUBJECT: 'submission_subject',
  /** aa_submission → aa_role (LOOKUP, optional — unlike aa_audition.role). */
  ROLE: 'submission_role',
  /** aa_submission → aa_project (LOOKUP). */
  PROJECT: 'submission_project',
  /** aa_submission → aa_media (LOOKUP, many — never master_detail). */
  MATERIALS: 'submission_materials',
  /** aa_submission → aa_person (LOOKUP). */
  CONTACT: 'submission_contact',
} as const;

/** aa_profile → aa_media (LOOKUP). The pack's own `profile_current_reel`,
 * read only here (lib/auditionSpread.ts's shared AA/LINK object carries the
 * headshot pointer alone, scoped to the four A4 screens) — B20a §5 needs it
 * for the performance discipline's own default tick, "the current headshot
 * + the lead reel". */
export const PROFILE_CURRENT_REEL = 'profile_current_reel';

/** The engine writes exactly this link when a submission converts
 * (`convertRecord`): src = the new audition, dst = the submission. F1's own
 * header — surfaced on the read model as `convertedToAuditionId`, never a
 * second lifecycle state. */
export const CONVERTED_FROM_LINK = 'converted_from';

export function otherEndOf(links: LinkRow[], id: string, linkType: string): string | null {
  const hit = links.find((l) => l.src_id === id && l.link_type === linkType);
  return hit?.dst_id ?? null;
}

// ---------------------------------------------------------------------
// THE STRIP — "All · Drafts · Sent · Invited · Closed" (B20a §2). Closed
// merges the pack's own `passed`/`archived` states, which is the one place
// this file states a fact about the pack's vocabulary rather than reading
// it — the alternative (spineFromLifecycle) draws an advancement line, not
// a merged bucket, and there is no way to derive "these two count as one
// filter" structurally. If the pack ever renamed either state, this list —
// not a silent empty filter — is where that would need to be found.
// ---------------------------------------------------------------------
export type SubmissionFilter = 'all' | 'draft' | 'sent' | 'invited' | 'closed';

export const SUBMISSION_FILTERS: { id: SubmissionFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'draft', label: 'Drafts' },
  { id: 'sent', label: 'Sent' },
  { id: 'invited', label: 'Invited' },
  { id: 'closed', label: 'Closed' },
];

export function filterOf(lifecycleState: string | null): SubmissionFilter {
  if (lifecycleState === 'passed' || lifecycleState === 'archived') return 'closed';
  if (lifecycleState === 'draft' || lifecycleState === 'sent' || lifecycleState === 'invited') {
    return lifecycleState;
  }
  return 'all';
}

export function matchesFilter(lifecycleState: string | null, filter: SubmissionFilter): boolean {
  return filter === 'all' || filterOf(lifecycleState) === filter;
}

/** B21 — the household's own funnel is still open, as opposed to decided
 * (`closed`) or unrecognised (`all`'s own fallback). HouseholdAuditions'
 * compact "Submissions" group on "All" shows only these, capped at 3 —
 * a submission somebody already passed on or put away belongs behind the
 * "All submissions →" door, not on the page you land on every day. */
export function isOpenSubmission(lifecycleState: string | null): boolean {
  return lifecycleState === 'draft' || lifecycleState === 'sent' || lifecycleState === 'invited';
}

/** The complement — `passed`/`archived`. Named rather than inlined so the
 * "or any closed ones" half of the B21 §2 rule reads as a fact about the
 * pack's vocabulary, not a regex somebody has to re-derive. */
export function isClosedSubmission(lifecycleState: string | null): boolean {
  return filterOf(lifecycleState) === 'closed';
}

/** "Sent Tue, Sep 29" — the list row's own date word (B20a §2). Plain
 * `toLocaleDateString`, UTC, so it never depends on the reader's clock
 * disagreeing with the server's stamp by a few hours near midnight. */
export function sentDateWord(sentAt: string | null): string | null {
  if (!sentAt) return null;
  const d = new Date(sentAt);
  if (Number.isNaN(d.getTime())) return null;
  return `Sent ${d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' })}`;
}

/** The stepper spine for the submission's own record — the fetched-schema
 * derivation every other stepper in this pack uses, unchanged
 * (auditionSpread.ts's own `stepsFor`: "the pack is the vocabulary, this
 * file holds no copy of it"). Re-exported under this module so the two
 * screens below have one import path for everything submissions-shaped. */
/** The submission's stage row. `passed` and `archived` are ENDINGS reached by
 * the quiet actions under the row ("Passed", "No word — put it away"), never
 * dots on it. The pack's `invited → passed` edge makes `stepsFor` put
 * `passed` on the spine, so the walk (2026-09-26) showed "Passed" twice —
 * once as a dot, once as the action. An ending shows as a dot only when the
 * submission is actually there, exactly like an audition's off-spine terminus. */
const SUBMISSION_ENDINGS = new Set(['passed', 'archived']);
export function submissionSteps(lifecycle: SchemaLifecycle | undefined, state: string | null): Step[] {
  const steps = stepsFor(lifecycle, state);
  return steps.filter((s) => !SUBMISSION_ENDINGS.has(s.state) || s.current);
}

export function strField(rec: RecordRow | null | undefined, name: string): string | null {
  const v = rec?.fields?.[name];
  return typeof v === 'string' && v.length > 0 ? v : null;
}
